import globalStore from '../store/GlobalStore';

const DAY = 24 * 60 * 60 * 1000;

export const getTimezoneOffset = (): any => {
  const { timezoneOffset } = globalStore.getState();
  return {
    hours: timezoneOffset ? Math.floor(timezoneOffset / 60) : 0,
    minutes: timezoneOffset ? timezoneOffset - Math.floor(timezoneOffset / 60) * 60 : 0,
    miliseconds: timezoneOffset * 60 * 1000,
    origin: timezoneOffset,
  };
};

export const getLocalTimezoneOffset = (): any => {
  const localTimezoneOffset = new Date().getTimezoneOffset();
  return {
    hours: Math.floor(localTimezoneOffset / 60),
    minutes: localTimezoneOffset - Math.floor(localTimezoneOffset / 60) * 60,
    miliseconds: localTimezoneOffset * 60 * 1000,
    origin: localTimezoneOffset,
  };
};

export const getTimezoneDiff = (): number => {
  const { timezoneOffset } = globalStore.getState();
  const localTimezoneOffset = new Date().getTimezoneOffset();

  let diff = 0;

  if (timezoneOffset >= 0 && localTimezoneOffset <= 0) {
    diff = timezoneOffset - localTimezoneOffset;
  }

  if (timezoneOffset >= 0 && localTimezoneOffset >= 0) {
    if (timezoneOffset > localTimezoneOffset) {
      diff = timezoneOffset - localTimezoneOffset;
    } else {
      diff = localTimezoneOffset - timezoneOffset;
    }
  }

  if (timezoneOffset <= 0 && localTimezoneOffset <= 0) {
    if (timezoneOffset < localTimezoneOffset) {
      diff = localTimezoneOffset - timezoneOffset;
    } else {
      diff = timezoneOffset - localTimezoneOffset;
    }
  }

  if (timezoneOffset <= 0 && localTimezoneOffset >= 0) {
    diff = timezoneOffset - localTimezoneOffset;
  }

  return diff * 60 * 1000;
};

export function getHoursAndMinutes(date?: string | Date): string {
  if (!date) return 'N/A';

  const d = new Date(date);
  const minutes = d.getMinutes() < 10 ? `0${d.getMinutes()}` : d.getMinutes();
  const hours = d.getHours() < 10 ? `0${d.getHours()}` : d.getHours();
  return `${hours}:${minutes}`;
}

export function getTime(date?: string | Date): string {
  if (!date) return 'N/A';

  const d = new Date(date);
  const seconds = d.getSeconds() < 10 ? `0${d.getSeconds()}` : d.getSeconds();
  const minutes = d.getMinutes() < 10 ? `0${d.getMinutes()}` : d.getMinutes();
  const hours = d.getHours() < 10 ? `0${d.getHours()}` : d.getHours();
  return `${hours}:${minutes}:${seconds}`;
}

export function getTimeZone(date?: string | Date): string {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return 'Invalid Date';

  const offset = d.getTimezoneOffset();
  const totalMinutes = Math.abs(offset);
  const hours = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
  const minutes = (totalMinutes % 60).toString().padStart(2, '0');
  const sign = offset > 0 ? '-' : '+';

  return `UTC${sign}${hours}:${minutes}`;
}

export function getMinutesAndSeconds(date?: string | Date): string {
  if (!date) return 'N/A';

  const d = new Date(date);
  const minutes = d.getMinutes() < 10 ? `0${d.getMinutes()}` : d.getMinutes();
  const seconds = d.getSeconds() < 10 ? `0${d.getSeconds()}` : d.getSeconds();
  return `${minutes}:${seconds}`;
}

export function getFormattedDate(date: string | Date): string {
  const d = new Date(date);
  return date ? d.toLocaleDateString(
    'en-GB',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    },
  ) : 'N/A';
}

export function getFormattedDateWithComma(date: string | Date): string {
  const d = getFormattedDate(date);
  return d.replace(/(\d{1,2})\s(.{3})\s(\d{4})/, '$1 $2, $3');
}

export const getFormattedDateAndTime = (date: string | Date, seconds?: boolean, milliseconds?: boolean): string => {
  const d = new Date(date);
  let formattedDate = date ? d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: seconds ? '2-digit' : undefined,
  }) : 'N/A';
  if (milliseconds) {
    const millis = d.getMilliseconds().toString().padStart(3, '0');
    formattedDate += `:${millis}`;
  }
  return formattedDate;
};

export const getPrevMonthDate = (): string => {
  const date = new Date(new Date().setUTCHours(getTimezoneOffset().hours, getTimezoneOffset().minutes, 0, 0));
  // we decided to use 30 days as fixed month
  // date.setMonth(date.getMonth() - 1);
  date.setDate(date.getDate() - 30);
  return date.toISOString();
};

export const getStartOfTheDay = (customDate?: Date): Date => {
  // const date = new Date(customDate || Date.now());
  // date.setUTCHours(0, 0, 0, 0);
  // const dayStart = new Date(date.getTime() + getTimezoneOffset().miliseconds);
  // return (dayStart.getDate() !== new Date().getDate()) ? new Date(dayStart.getTime() + DAY) : dayStart;
  const offnow = new Date((customDate || new Date()).valueOf() - getTimezoneOffset().miliseconds);
  const dayStart = new Date(Date.UTC(offnow.getUTCFullYear(), offnow.getUTCMonth(), offnow.getUTCDate(), 0, 0, 0, 0) + getTimezoneOffset().miliseconds);
  return dayStart;
};

export const getEndOfTheDay = (customDate?: Date): Date => {
  // const date = new Date(customDate || Date.now());
  // date.setUTCHours(23, 59, 59, 999);
  // const dayEnd = new Date(date.getTime() + getTimezoneOffset().miliseconds);
  // return (dayEnd.getDate() !== new Date().getDate()) ? new Date(dayEnd.getTime() + DAY) : dayEnd;
  const offnow = new Date((customDate || new Date()).valueOf() - getTimezoneOffset().miliseconds);
  const dayEnd = new Date(Date.UTC(offnow.getUTCFullYear(), offnow.getUTCMonth(), offnow.getUTCDate(), 23, 59, 59, 999) + getTimezoneOffset().miliseconds);
  return dayEnd;
};

export function getStartOfWeek(weekStartDay: number = 1): Date {
  const offnow = new Date(new Date().valueOf() - getTimezoneOffset().miliseconds);
  const weekDay = offnow.getUTCDay();
  const week = new Date(offnow.getTime() - ((weekDay - weekStartDay + 7) % 7) * DAY);
  const dayStartOfWeek = new Date(Date.UTC(week.getUTCFullYear(), week.getUTCMonth(), week.getUTCDate(), 0, 0, 0) + getTimezoneOffset().miliseconds);
  return dayStartOfWeek;
}

export function getEndOfWeek(weekStartDay: number = 1): Date {
  const offnow = new Date(new Date().valueOf() - getTimezoneOffset().miliseconds);
  const weekDay = offnow.getUTCDay();
  const daysUntilEnd = (weekStartDay + 6 - weekDay) % 7;
  const week = new Date(offnow.getTime() + daysUntilEnd * DAY);
  const dayEndOfWeek = new Date(Date.UTC(week.getUTCFullYear(), week.getUTCMonth(), week.getUTCDate(), 23, 59, 59, 999) + getTimezoneOffset().miliseconds);
  return dayEndOfWeek;
}

export function getEndOfMonth(): Date {
  const offnow = new Date(new Date().valueOf() - getTimezoneOffset().miliseconds);
  const lastDay = new Date(Date.UTC(offnow.getUTCFullYear(), offnow.getUTCMonth() + 1, 0));
  const dayEndOfMonth = new Date(Date.UTC(lastDay.getUTCFullYear(), lastDay.getUTCMonth(), lastDay.getUTCDate(), 23, 59, 59, 999) + getTimezoneOffset().miliseconds);
  return dayEndOfMonth;
}

export function getStartOfMonth(): Date {
  // const date = new Date(Date.now());
  // date.setUTCDate(1);
  // date.setUTCHours(0, 0, 0, 0);
  // const dayStartOfMonth = new Date(date.getTime() + getTimezoneOffset().miliseconds);
  // return (dayStartOfMonth.getDate() !== new Date().getDate()) ? new Date(dayStartOfMonth.getTime() + DAY) : dayStartOfMonth;
  const offnow = new Date(new Date().valueOf() - getTimezoneOffset().miliseconds);
  const dayStartOfMonth = new Date(Date.UTC(offnow.getUTCFullYear(), offnow.getUTCMonth(), 1, 0, 0, 0, 0) + getTimezoneOffset().miliseconds);
  return dayStartOfMonth;
}

export const getNow = (): Date => {
  const date = new Date(Date.now());
  return new Date(date.getTime() + getTimezoneOffset().miliseconds);
};

const PERIOD_RANGE_GETTERS: Record<string, () => { startDate: Date; endDate: Date }> = {
  daily: () => ({ startDate: getStartOfTheDay(), endDate: getEndOfTheDay() }),
  weekly: () => ({ startDate: getStartOfWeek(), endDate: getEndOfWeek() }),
  monthly: () => ({ startDate: getStartOfMonth(), endDate: getEndOfMonth() }),
};

// Filters with period daily/weekly/monthly persist startDate/endDate computed at the time they
// were set. Once that day/week/month has passed, the stored range is stale until the user
// re-clicks the same period button. This recomputes the range so a stale filter set self-heals.
export const refreshPeriodDates = <T extends Record<string, string[] | string> | undefined>(
  filters: T,
): T => {
  if (!filters) return filters;
  const period = (filters as Record<string, string[]>).period?.[0];
  const rangeGetter = period ? PERIOD_RANGE_GETTERS[period] : undefined;
  if (!rangeGetter) return filters;

  const { startDate, endDate } = rangeGetter();
  const currentStartDate = (filters as Record<string, string[]>).startDate?.[0];
  const currentEndDate = (filters as Record<string, string[]>).endDate?.[0];
  if (currentStartDate === startDate.toISOString() && currentEndDate === endDate.toISOString()) {
    return filters;
  }

  return {
    ...filters,
    startDate: [startDate.toISOString()],
    endDate: [endDate.toISOString()],
  };
};

const PERIOD_TO_LABEL: Record<string, string> = {
  daily: 'Day',
  weekly: 'Week',
  monthly: 'Month',
};

// Mirrors TimeSelection's Day/Week/Month/All/Custom button labels, so widgets showing an
// aggregate (e.g. "Average score") can reflect which period filter produced the value.
// "All" and "Custom" both persist as period: 'custom' - they're told apart by startDate,
// since "All" always sets it to the epoch (see TimeSelection.handleSelectionClick).
export const getPeriodSelectionLabel = (
  filters: Record<string, string[] | string> | undefined,
): string => {
  const period = filters?.period?.[0] as string | undefined;
  if (period && PERIOD_TO_LABEL[period]) {
    return PERIOD_TO_LABEL[period];
  }
  if (period === 'custom' && filters?.startDate?.[0] === new Date(0).toISOString()) {
    return 'All';
  }
  return 'Custom';
};

const PERIOD_TO_DISPLAY_LABEL: Record<string, string> = {
  daily: 'Today',
  weekly: 'This week',
  monthly: 'This month',
};

const formatShortDate = (date: string | Date): string => {
  const d = new Date(date);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).replace(/\//g, '.');
};

// Mirrors TimeSelection's Day/Week/Month/All/Custom button labels for use as a
// "<Widget> • <period>" suffix. Unlike getPeriodSelectionLabel, a genuine custom
// range (not the "All" epoch sentinel) renders as the actual picked date(s)
// instead of the word "Custom".
export const getPeriodDisplayLabel = (
  filters: Record<string, string[] | string> | undefined,
): string => {
  const period = filters?.period?.[0] as string | undefined;
  if (period && PERIOD_TO_DISPLAY_LABEL[period]) {
    return PERIOD_TO_DISPLAY_LABEL[period];
  }
  const startDate = filters?.startDate?.[0] as string | undefined;
  const endDate = filters?.endDate?.[0] as string | undefined;
  if (period === 'custom' && startDate === new Date(0).toISOString()) {
    return 'All time';
  }
  if (period === 'custom' && startDate) {
    const formattedStart = formatShortDate(startDate);
    const formattedEnd = endDate ? formatShortDate(endDate) : formattedStart;
    if (formattedStart === formattedEnd) {
      return formattedStart;
    }
    return `${formattedStart} – ${formattedEnd}`;
  }
  return 'All time';
};

// True when the active filter resolves to exactly one day - either the "Day" period button,
// or a "Custom" range whose start and end fall on the same calendar day. Widgets showing an
// aggregate score use this to swap their "Average score" label for "Daily score".
export const isSingleDayPeriod = (
  filters: Record<string, string[] | string> | undefined,
): boolean => {
  const period = filters?.period?.[0] as string | undefined;
  if (period === 'daily') {
    return true;
  }
  if (period !== 'custom') {
    return false;
  }
  const startDate = filters?.startDate?.[0] as string | undefined;
  const endDate = filters?.endDate?.[0] as string | undefined;
  if (!startDate || startDate === new Date(0).toISOString()) {
    return false;
  }
  return formatShortDate(startDate) === formatShortDate(endDate || startDate);
};

export const getDaysBetweenDates = (date1: string, date2: string): number => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return Math.round((d2.getTime() - d1.getTime()) / (1000 * 3600 * 24));
};

export const getSecBetweenDates = (date1: string, date2: string): number => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return Math.round((d2.getTime() - d1.getTime()) / 1000);
};

export const convertMsToTime = (ms: number): string => new Date(ms).toISOString().slice(11, 19);

export const convertSecToTimelabel = (seconds: number): string => {
  let interval = seconds / 31536000;
  if (interval > 1) {
    return `${Math.floor(interval)}y`;
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return `${Math.floor(interval)}m`;
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return `${Math.floor(interval)}d`;
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return `${Math.floor(interval)}h`;
  }
  interval = seconds / 60;
  if (interval > 1) {
    return `${Math.floor(interval)}min`;
  }
  return `${Math.floor(seconds)}s`;
};

export const getDeviceLastActivityLabel = (activeAt: string | null | undefined, isOnline: boolean): string => {
  if (!activeAt) {
    return 'No info about last activity';
  }
  if (!isOnline) {
    return `Last active: ${getFormattedDate(new Date(activeAt))}, ${getHoursAndMinutes(new Date(activeAt))}`;
  }
  const now = new Date(Date.now());
  const lastActive = new Date(activeAt);
  return `Uptime: ${convertSecToTimelabel((now.valueOf() - lastActive.valueOf()) / 1000)}`;
};

export const convertSecToTimeFullLabel = (seconds: number, noSec?: boolean): string => {
  let interval = seconds / 31536000;
  let leftSec = seconds - Math.floor(interval) * 31536000;
  let fullLabel = '';

  if (interval >= 1) {
    fullLabel += `${Math.floor(interval)}y `;
  }
  interval = leftSec / 2592000;
  leftSec -= Math.floor(interval) * 2592000;
  if (interval >= 1) {
    fullLabel += `${Math.floor(interval)}m `;
  }
  interval = leftSec / 86400;
  leftSec -= Math.floor(interval) * 86400;
  if (interval >= 1) {
    fullLabel += `${Math.floor(interval)}d `;
  }
  interval = leftSec / 3600;
  leftSec -= Math.floor(interval) * 3600;
  if (interval >= 1) {
    fullLabel += `${Math.floor(interval)}h `;
  }
  interval = leftSec / 60;
  leftSec -= Math.floor(interval) * 60;
  if (interval >= 1) {
    fullLabel += `${Math.floor((interval))}min${!noSec ? ' ' : ''}`;
  }
  if (!noSec) {
    fullLabel += `${leftSec}s`;
  }

  return fullLabel;
};

export const getLeftPeriodLabel = (date: string): string => {
  if (!date) {
    return '';
  }

  const seconds = Math.max(0, (new Date().valueOf() - new Date(date).valueOf()) / 1000);

  if (seconds <= 0) {
    return 'Just now';
  }

  return `${convertSecToTimelabel(seconds)} ago`;
};

export const convertMsToHours = (s: number): number => (Math.round(s / 360) / 10);

const getDateStrFromISOStr = (date: string): string => date.split('T')[0];

export const getTimeRangeText = (filter: any, useDefault?: boolean): string => {
  let startDate = filter?.startDate?.[0];
  let endDate = filter?.endDate?.[0];
  const period = filter?.period[0];

  if (useDefault && (!startDate || !endDate)) {
    endDate = getNow().toISOString();
    startDate = getPrevMonthDate();
  }

  if (!startDate) {
    return '';
  }

  let periodDays;
  switch (period) {
    case 'weekly':
      periodDays = 7;
      break;
    case 'daily':
      periodDays = 1;
      break;
    case 'monthly':
    default:
      periodDays = 30;
  }

  const endDateCalc = endDate && endDate.length ? endDate : new Date(Date.now()).toISOString();

  return `${getDateStrFromISOStr(startDate)}_${getDateStrFromISOStr(endDateCalc)}`;
};

export const getDateWeek = (date: Date): number => {
  const currentDate = (typeof date === 'object') ? date : new Date();
  const januaryFirst = new Date(currentDate.getFullYear(), 0, 1);
  const daysToNextMonday = (januaryFirst.getDay() === 1) ? 0 : (7 - januaryFirst.getDay()) % 7;
  const nextMonday = new Date(currentDate.getFullYear(), 0, januaryFirst.getDate() + daysToNextMonday);
  if (currentDate < nextMonday) {
    return 52;
  }
  return (currentDate > nextMonday ? Math.ceil((currentDate.getTime() - nextMonday.getTime()) / (24 * 3600 * 1000) / 7) : 1);
};

export const formatDurationFromSeconds = (totalSeconds: number): string => {
  const seconds = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${mins}m ${secs}s`;
  }
  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs}s`;
};

export const formatDurationFromRange = (
  fromTimestamp?: number | null,
  toTimestamp?: number | null,
): string | null => {
  if (!fromTimestamp || !toTimestamp || toTimestamp <= fromTimestamp) return null;
  return formatDurationFromSeconds((toTimestamp - fromTimestamp) / 1000);
};

export const getElapsedTimeLabel = (startTime?: string | null): string => {
  if (!startTime) {
    return '...';
  }
  const diffInSeconds = (Date.now() - Date.parse(startTime)) / 1000;
  if (!Number.isFinite(diffInSeconds) || diffInSeconds < 0) {
    return '...';
  }
  const hours = Math.floor(diffInSeconds / 3600);
  const mins = Math.floor((diffInSeconds - hours * 3600) / 60);
  const sec = Math.floor(diffInSeconds - hours * 3600 - mins * 60);

  return `${hours ? `${hours}h ` : ''}${mins}m ${sec}s`;
};

export const getTripDuration = (startTime: string | undefined, endTime: string | undefined | null): string => {
  if (!startTime) {
    return '...';
  }
  const realEndTime = endTime || new Date().toISOString();
  const diffInSeconds = (Date.parse(realEndTime) - Date.parse(startTime)) / 1000;
  if (!Number.isFinite(diffInSeconds) || diffInSeconds <= 0) {
    return 'N/A';
  }
  const days = Math.floor(diffInSeconds / 86400);
  const hours = Math.floor((diffInSeconds - days * 86400) / 3600);
  const mins = Math.floor((diffInSeconds - days * 86400 - hours * 3600) / 60);
  const sec = Math.floor(diffInSeconds - days * 86400 - hours * 3600 - mins * 60);
  const duration = `${days ? `${days}d ` : ''}${hours ? `${hours}h ` : ''}${mins && !days ? `${mins}m ` : ''}${sec && !(days || hours) ? `${sec}s` : ''}`;
  return duration || '0s';
};
